(ns ^:dev/always bp-clients.tv-app.auth.events
  "Namespace for re-frame events related to authentication"
  (:require
   [bp-clients.tv-app.api.events :as api]
   [bp-clients.tv-app.api.graphql.purgatorium :as purgatorium-queries]
   [bp-clients.tv-app.api.graphql.user :as user]
   [bp-clients.tv-app.common.events :as common]
   [bp-clients.tv-app.router :as router]
   [bp-clients.tv-app.utils.logger :as logger]
   [clojure.string :refer [join]]
   [day8.re-frame.http-fx]
   [re-frame.core :as rf :refer [after inject-cofx reg-cofx reg-event-fx reg-event-db path]]))

(def auth-interceptors
  [(path ::auth)])

(def ^:private max-request-retry-attempt
  "Numbers of retries before failing completly"
  5) ;; maybe have this come from a config

(def ^:private polling-interval-ms
  "Polling interval used to check whether user has logged in"
  6000) ;; maybe have this come from a config

(def auth-token-path
  "State token path"
  [:user :token])

(defn set-token-db!
  "Sets user token in indexdb"
  [{::keys [auth] :as _db}]
  (when-let [token (get-in auth auth-token-path)]
    (common/indexdb-put {:table common/user-table
                         :column common/user-api-key
                         :value token})
    nil))

(defn remove-token-db!
  "Removes user token from indexdb"
  []
  (common/indexdb-put {:table common/user-table
                       :column common/user-api-key
                       :value nil})
  nil)

(defn pre-init-db
  "Recovers user token from local-storage"
  [db [_event-name event]]
  (common/indexdb-get {:table common/user-table
                       :column common/user-api-key
                       :init? true
                       :success event})
  db)

(defn get-token-success
  [db [_event-name token]]
  (assoc-in db auth-token-path token))

(defn auth-token-co-fx
  "Given an effect map. Adds :auth-token to it."
  [cofx _]
  (assoc cofx ::auth-token (get-in cofx (apply merge [:db ::auth] auth-token-path))))

(defn login-ev
  "Given a state effect. Dispatches the logged-in event."
  [{:keys  [db]} [_event-name redirect-home?]]
  (let [graphql-config {:auth-token (get-in db auth-token-path)}]
    {:db         (dissoc db :login) ;; clears login auth/codes
     :dispatch-n (cond-> [[::api/init-graphql-with-auth graphql-config]
                          [::load-user-data]
                          [::load-initial-data]
                          [:bp-clients.tv-app.nav.events/fetch-menu]]
                   (true? redirect-home?) (merge [::router/route-changed {:redirect ::router/home}]))}))

(defn load-user-data
  "Loads the user data"
  [_ [_event-name _event-data]]
  {:dispatch [::api/query-graphql
              (merge {:instance-id :auth
                      :on-success  ::load-user-data-success
                      :on-error    [::load-user-data-error]}
                     (user/user-info-query))]})

(defn load-user-data-success
  "Success event handler for the ::fetch-login-token"
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  (let [decoded-data (decoder data)]
    (logger/set-person! (select-keys decoded-data [:email :name :roles]))
    {:db (assoc-in db [:user :profile] decoded-data)}))

(defn load-user-data-error
  "Fetches the session code the user must enter to login."
  ;; @todo
  [db [_event-name _error]]
  db)

(defn load-initial-data
  "Loads the app initial data"
  [{:keys [db]} [_event-name _event-data]]
  {:db db})

(defn logout-ev
  "Given a state effect. Logs out user, dismisses loaded data and active-nav* items."
  [{:keys [db]} [_event-name _event-data]]
  (logger/set-person! nil)
  {:db         (dissoc db :user)
   :dispatch-n [[::api/destroy-graphql :auth]
                [::sign-in]]})

(defn session-login-polling-error
  "Error event handler for the ::session-login-polling"
  [{:keys [db]} [_event-name _event-data]]
  (let [{:keys [login] :as new-db} (update-in db [:login :attempt] inc)
        should-retry?              (< (:attempt login) max-request-retry-attempt)
        failure-events             [[::session-login-polling {:cancel? true}]
                                    ;; show error page @todo
                                    [::fatal-login-error]]]
    (cond-> {:db new-db}
      (not should-retry?) (assoc :dispatch-n failure-events))))

(defn session-login-polling-success
  "Success event handler for the ::session-login-polling"
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  (let [{:keys [session_valid token]} (decoder data)
        logged-in?                    (boolean (and session_valid (seq token)))]
    (cond-> {:db (cond-> db
                   (not logged-in?) (assoc-in [:login :attempt] nil) ;; clears login attemmps
                   (seq token)      (assoc-in auth-token-path token)
                   logged-in?       (dissoc :login))}
      logged-in? (merge {:dispatch-n [[::session-login-polling {:cancel? true}]
                                      [::api/destroy-graphql :no-auth]
                                      [::login true]]}))))

(defn session-login-polling
  "Starts/stops requests polling to know whether the user has logged."
  [{:keys [db]} [_event-name {:keys [cancel?] :as _config}]]
  (let [id     ::login-code-interval
        start? (not cancel?)
        event  [::api/query-graphql (merge {:instance-id :no-auth
                                            :on-success  ::session-login-polling-success
                                            :on-error    [::session-login-polling-error]}
                                           (purgatorium-queries/token-valid-query (:login db)))]]
    (cond-> {:db db}
      cancel? (merge {::common/interval {:action :end
                                         :id     id}})
      start?  (merge {::common/interval {:action    :start
                                         :id        id
                                         :frequency polling-interval-ms
                                         :event     event}}))))

(defn init-login-session
  "Checks whether the session has been initialized and a login code/secret set."
  [{:keys [db]} [_event-name _event-data]]
  (let [{:keys [code secret]} (:login db)
        session-started?      (and (seq code) (seq secret))]
    (cond-> {:db db
             :dispatch [::fetch-login-token]}
      session-started? (merge {:dispatch [::session-login-polling]}))))

(defn fetch-login-token-ev
  "Fetches the session code the user must enter to login."
  [{:keys [db]} [_event-name _event-data]]
  {:db         (update db :login #(-> %
                                      (dissoc :code)
                                      (dissoc :secret)
                                      (dissoc :error?)
                                      (dissoc :qr-url)))
   :dispatch-n [[::session-login-polling {:cancel? true}]
                [::api/mutate-graphql
                 (merge {:instance-id :no-auth
                         :on-success  ::fetch-login-token-success
                         :on-error    [::fetch-login-token-error]}
                        (purgatorium-queries/init-session-mutation))]]})

(defn fetch-login-token-success-ev
  "Success event handler for the ::fetch-login-token"
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  {:db         (-> db
                   (dissoc :login) ;; clears login
                   (assoc :login (decoder data)))
   :dispatch-n [[::generate-qr-code]
                [::init-login-session]]})

(defn generate-qr-code-ev
  "Generates the QR code url"
  [db [_event-name _event-data]]
  (let [{:keys [code]} (:login db)
        qr-code-url    (join "/" [api/tv-login-url code])
        code?          (seq code)]
    (cond-> db
      code? (assoc-in [:login :qr-url] qr-code-url))))

(defn fetch-login-token-error-ev
  "Error event handler for the ::fetch-login-token"
  [{:keys [db]} [_event-name _event-data]]
  (let [{:keys [login] :as new-db} (update-in db [:login :attempt] inc)
        shoud-retry?               (< (:attempt login) max-request-retry-attempt)]
    (cond-> {:db new-db
             ;; show error page @todo
             :dispatch [::fatal-login-error]}
      shoud-retry? (merge {:dispatch [::fetch-login-token]}))))

(defn sign-in-ev
  "Starts graphql for unauth queries, inits the login flow "
  [{:keys [db]}]
  {:db         db
   :dispatch-n [[::api/init-graphql-no-auth]
                [::init-login-session]
                [::router/route-changed {:redirect ::router/home}]]})

(defn fatal-login-error-ev
  [db]
  (update db :login #(-> %
                         (assoc :error? true)
                         (dissoc :code)
                         (dissoc :secret)
                         (dissoc :qr-url))))

(reg-cofx ::auth-token auth-token-co-fx)
(reg-event-fx ::fetch-login-token auth-interceptors fetch-login-token-ev)
(reg-event-fx ::fetch-login-token-error auth-interceptors fetch-login-token-error-ev)
(reg-event-fx ::fetch-login-token-success auth-interceptors fetch-login-token-success-ev)

(reg-event-db ::generate-qr-code auth-interceptors generate-qr-code-ev)
(reg-event-fx ::init-login-session auth-interceptors init-login-session)
(reg-event-fx ::load-initial-data auth-interceptors load-initial-data)
(reg-event-fx ::load-user-data auth-interceptors load-user-data)
(reg-event-fx ::load-user-data-success auth-interceptors load-user-data-success)
(reg-event-db ::load-user-data-error auth-interceptors load-user-data-error)

(reg-event-fx ::login (apply merge [(inject-cofx ::auth-token)] auth-interceptors) login-ev)
(reg-event-fx ::logout (apply merge [(after remove-token-db!)] auth-interceptors) logout-ev)
(reg-event-fx ::session-login-polling auth-interceptors session-login-polling)
(reg-event-fx ::session-login-polling-error auth-interceptors session-login-polling-error)
(reg-event-fx ::session-login-polling-success (apply merge [(after set-token-db!)] auth-interceptors) session-login-polling-success)
(reg-event-db ::fatal-login-error auth-interceptors fatal-login-error-ev)
(reg-event-fx ::sign-in  auth-interceptors sign-in-ev)
(reg-event-db ::pre-init-db auth-interceptors pre-init-db)
(reg-event-db ::get-token-success auth-interceptors get-token-success)

(comment
  ;; to manually logout
  (rf/dispatch [::logout])

  ;; to manually cancel login request polling
  (rf/dispatch [::session-login-polling {:cancel? true}])

  ;; to refresh token
  (rf/dispatch [::fetch-login-token]))
